import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from "@mui/material";
import {
  BillingMethod,
  BillingRuleConditionOperator,
  BillingRuleConditionTarget,
  BillingRuleType,
  GetCustomerDetailsQuery,
  useAddBillingRuleMutation,
  useEditBillingRuleMutation,
} from "../../../graphql/generated";
import { useTranslation } from "react-i18next";
import BillingRuleForm from "../BillingRuleForm/BillingRuleForm";
import EnumSelect from "../../common/EnumSelect/EnumSelect";
import { BillingRuleItem } from "./BillingRulesEditor";
import { defaultCopyEffectiveDates } from "../EffectiveDates/defaultCopyEffectiveDates";

const getEmptyRule = (type: BillingRuleType): BillingRuleItem => ({
  _id: "",
  amount: null,
  conditions: [
    {
      target: BillingRuleConditionTarget.TrailerType,
      operator: BillingRuleConditionOperator.Equals,
      value: null,
      parameters: [],
    },
  ],
  customer: null,
  customFields: [],
  effectiveEndDate: null,
  effectiveStartDate: null,
  isActive: true,
  label: "",
  method: null,
  priority: 0,
  rates: [
    {
      method: BillingMethod.PerMile,
      amount: 0,
      label: "",
      referenceNumber: "",
      parameters: [],
    },
  ],
  referenceNumber: "",
  type,
});

const cloneBillingRuleForCopy = (rule: BillingRuleItem): BillingRuleItem => ({
  ...rule,
  _id: "",
  isActive: true,
  ...defaultCopyEffectiveDates(),
});

type BillingRuleFormDialogProps = {
  open: boolean;
  customer: GetCustomerDetailsQuery["customerById"];
  initialType: BillingRuleType;
  initialRule: BillingRuleItem | null;
  mode: "create" | "edit" | "copy";
  addBillingRuleMutation: ReturnType<typeof useAddBillingRuleMutation>;
  editBillingRuleMutation: ReturnType<typeof useEditBillingRuleMutation>;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
  enqueueSnackbar: (message: string, options?: { variant: "success" | "error" }) => void;
};

const BillingRuleFormDialog = ({
  open,
  customer,
  initialType,
  initialRule,
  mode,
  addBillingRuleMutation,
  editBillingRuleMutation,
  onClose,
  onSaved,
  enqueueSnackbar,
}: BillingRuleFormDialogProps) => {
  const testIds = {
    typeSelect: "billing-rules-editor-dialog-type-select",
    cancelBtn: "billing-rules-editor-dialog-cancel-btn",
    saveBtn: "billing-rules-editor-dialog-save-btn",
  };
  const { t } = useTranslation("billingRules");
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState<BillingRuleItem | null>(null);

  const effectiveInitialRule = useMemo(() => {
    if (!initialRule) {
      return getEmptyRule(initialType);
    }
    return mode === "copy" ? cloneBillingRuleForCopy(initialRule) : initialRule;
  }, [initialRule, initialType, mode]);

  useEffect(() => {
    if (open) {
      setDraft(null);
    }
  }, [open, initialRule, initialType, mode]);

  const ruleDraft = draft || effectiveInitialRule;

  const isValid =
    !!ruleDraft.type &&
    (ruleDraft.conditions?.length || 0) > 0 &&
    (ruleDraft.rates?.length || 0) > 0;

  const title =
    mode === "edit"
      ? t("edit", "Edit")
      : mode === "copy"
      ? t("copyBillingRule", "Copy this billing rule")
      : t("addBillingRule", "Add Billing Rule");

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <EnumSelect data-testid="billing-rule-form-dialog-billing-rule-type"
            enumObject={BillingRuleType}
            label={t("billingType", "Billing Type")}
            value={ruleDraft.type}
            onChange={(_, value) =>
              setDraft({
                ...ruleDraft,
                type: value || BillingRuleType.Primary,
              })
            }
            disabled={mode === "edit"}
            fullWidth
            required
            inputProps={{
              "data-testid": testIds.typeSelect,
            }}
          />
          <BillingRuleForm billingRule={ruleDraft} customer={customer} onChange={setDraft} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          data-testid={testIds.cancelBtn}
          onClick={onClose}
          disabled={saving}
          color="inherit"
        >
          {t("cancel", "Cancel")}
        </Button>
        <Button
          data-testid={testIds.saveBtn}
          variant="contained"
          disabled={saving || !isValid}
          onClick={async () => {
            try {
              setSaving(true);
              if (mode === "edit" && initialRule?._id) {
                await editBillingRuleMutation.mutateAsync({
                  id: initialRule._id,
                  editBillingRuleData: {
                    type: ruleDraft.type,
                    conditions: ruleDraft.conditions,
                    rates: ruleDraft.rates,
                    label: ruleDraft.label,
                    referenceNumber: ruleDraft.referenceNumber,
                    effectiveStartDate: ruleDraft.effectiveStartDate,
                    effectiveEndDate: ruleDraft.effectiveEndDate,
                    isActive: ruleDraft.isActive,
                    customFields: ruleDraft.customFields,
                  },
                });
              } else {
                await addBillingRuleMutation.mutateAsync({
                  newBillingRuleData: {
                    customer: customer?._id,
                    type: ruleDraft.type,
                    conditions: ruleDraft.conditions,
                    rates: ruleDraft.rates,
                    label: ruleDraft.label,
                    referenceNumber: ruleDraft.referenceNumber,
                    effectiveStartDate: ruleDraft.effectiveStartDate,
                    effectiveEndDate: ruleDraft.effectiveEndDate,
                    isActive: ruleDraft.isActive,
                    customFields: ruleDraft.customFields,
                  },
                });
              }
              enqueueSnackbar(t("saveChanges", "Save Changes"), { variant: "success" });
              await onSaved();
              onClose();
            } catch (error) {
              enqueueSnackbar(
                (error as Error).message || t("error.title", "Error"),
                { variant: "error" }
              );
            } finally {
              setSaving(false);
            }
          }}
        >
          {t("saveChanges", "Save Changes")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BillingRuleFormDialog;
